import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  SmoothStepEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import Sidebar from "./Sidebar";
import "./Flow.css";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../../../assets/css/templateBuilder.css";
import "../../../assets/css/addunit.css";
import "../../../assets/css/mainMenu.css";

import search1 from "../../../assets/icons/search1.svg";
import close from "../../../assets/icons/close.svg";
import folder from "../../../assets/icons/folder.svg";


import ImageNode from "./ImageNode";
import DefaultNode from "./DefaultNode";
import Loader from "../../../Components/LoaderAndSpinner/Loader";

import fetchWrapper from "../../../AuthMiddleware/FetchWrapper";

const EditAsset = () => {
  const [loading, setLoading] = useState(false);
  const reactFlowWrapper = useRef(null);
  const [master, setMaster] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null); // Track selected node
  const [editData, setEditData] = useState({}); // Track data to be edited
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
  });
  const [usedTemplateTypeIds, setUsedTemplateTypeIds] = useState([]); // State to track used nodes
  const [edgeToDelete, setEdgeToDelete] = useState(null); // Track edge to delete
  const [edgeLabelModal, setEdgeLabelModal] = useState({
    isOpen: false,
    label: "",
    edgeId: null,
  }); // New state for edge label modal
  const templateNameRef = useRef(null);
  const [templateName, setTemplateName] = useState("");
  const [templatecode, setTemplateCode] = useState("");
  const [templateNameInput, setTemplateNameInput] = useState("");
  const { template_type_id, model_id } = useParams();
  const navigate = useNavigate();

  const nodeTypes = useMemo(
    () => ({
      imageNode: ImageNode,
      defaultNode: DefaultNode,
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      smoothstep: SmoothStepEdge,
    }),
    []
  );

  const fetchNodes = async () => {
    const apiUrl = `${process.env.REACT_APP_BASE_API_URL1}/api/v1/template-by-type`;



    try {
      const data = await fetchWrapper(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ template_type_id }), // Ensure template_type_id is defined
      });


      // const processedData = data.map((item) => ({
      //   templateType: item.templateType,
      //   templates: item.templates,
      //   master: item.master,
      // }));
      // setMaster(processedData);

      setMaster(data);
    } catch (error) {
      console.error("Fetch error:", error);
      setMaster([]);
    }
  };

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed },
            label: "",
          },
          eds
        )
      ),
    []
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!reactFlowWrapper.current) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const masterId = event.dataTransfer.getData("application/reactflow");

      // Ensure the drop happens within the bounds of the canvas
      if (
        typeof masterId === "undefined" ||
        !masterId ||
        !reactFlowInstance ||
        !master.length
      ) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      // const masterDataItem = master.masters.find((m) => m._id === masterId);

      const masterDataItem = master.find((templateType) =>
        templateType.templates.some((template) => template._id === masterId)
      );

      const foundTemplate = masterDataItem
        ? masterDataItem.templates.find((template) => template._id === masterId)
        : null;



      if (!masterDataItem) {
        console.warn(`Master item not found for ID: ${masterId}`);
        return;
      }

      const newNode = {
        id: `${masterId}`, // Use `masterId` as the unique identifier
        type: "defaultNode", // Determine the node type
        position,
        sourcePosition: "right",
        targetPosition: "left",
        data: {
          label: foundTemplate.template_name, // Use correct name
          img: foundTemplate.img, // Include image if present
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setUsedTemplateTypeIds((prev) => [
        ...prev,
        masterDataItem.templateType._id,
      ]); // Add the template type ID to the used list
    },
    [reactFlowInstance, master, nodes, usedTemplateTypeIds] // Dependencies
  );

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
    setSelectedNode(null); // Reset the selected node
    setEditData({}); // Reset edit data
  };

  const handleEditNodeData = (key, value) => {
    // Update the temporary edit data
    setEditData((prevData) => ({ ...prevData, [key]: value }));
  };

  const saveChanges = () => {
    if (!selectedNode) return;

    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? {
            ...node,
            data: { ...node.data, label: editData.label },
            sourcePosition: editData.sourcePosition,
            targetPosition: editData.targetPosition,
          }
          : node
      )
    );

    closeModal(); // Close modal after saving
  };

  const openEdgeLabelModal = (edgeId) => {
    // Fetch the correct edge from the current state
    const edge = edges.find((e) => e.id === edgeId);
    const currentLabel = edge?.label || ""; // Get existing label or default to empty
    setEdgeLabelModal({ isOpen: true, label: currentLabel, edgeId }); // Open modal with current label
  };

  // Function to handle adding a label to an edge
  const addLabelToEdge = (edgeId, label) => {
    setEdges((eds) =>
      eds.map((edge) =>
        edge.id === edgeId
          ? { ...edge, label } // Update edge with new label
          : edge
      )
    );
    setEdgeLabelModal({ isOpen: false, label: "", edgeId: null }); // Close modal after updating label
  };

  const onEdgeClick = (event, edge) => {
    event.stopPropagation(); // Stop propagation to avoid interfering with other clicks

    if (!reactFlowWrapper.current) {
      return;
    }

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const xPosition = event.clientX - reactFlowBounds.left;
    const yPosition = event.clientY - reactFlowBounds.top;
    setEdgeToDelete({ id: edge.id, position: { x: xPosition, y: yPosition } }); // Capture edge ID and position
  };

  const onDeleteEdge = () => {
    if (edgeToDelete) {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeToDelete.id)); // Delete the specified edge
      setEdgeToDelete(null); // Reset the edge to delete
    }
  };

  // Event handler for right-click on a node
  const onNodeContextMenu = (event, node) => {
    event.preventDefault(); // Prevent the default context menu

    if (!reactFlowWrapper.current) {
      return;
    }

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const yOffset = 150;

    setContextMenu({
      isOpen: true,
      position: {
        x: event.clientX,
        y: event.clientY - reactFlowBounds.top + yOffset,
      },
    });
    setSelectedNode(node);
    setEditData({
      label: node.data.label,
      sourcePosition: node.sourcePosition,
      targetPosition: node.targetPosition,
    });
  };

  const closeContextMenu = () =>
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });

  const onDeleteNode = (nodeToDelete) => {
    // Helper function to get the template type ID based on the node ID
    const getTemplateTypeId = (master, templateId) => {
      for (const templateGroup of master) {
        const template = templateGroup.templates.find(
          (template) => template._id === templateId
        );
        if (template) {
          return template.template_type_id;
        }
      }
      return null; // return null if the template is not found
    };

    // Remove the node from the list of nodes
    setNodes((nds) => nds.filter((node) => node.id !== nodeToDelete.id));

    // Remove all edges connected to the node
    setEdges((eds) =>
      eds.filter(
        (edge) =>
          edge.source !== nodeToDelete.id && edge.target !== nodeToDelete.id
      )
    );



    const templateTypeId = getTemplateTypeId(master, nodeToDelete.id);

    if (templateTypeId) {
      setUsedTemplateTypeIds((used) =>
        used.filter((typeId) => typeId !== templateTypeId)
      );
    }

    closeContextMenu(); // Close the context menu after deletion
  };

  const hideButton = () => {
    if (edgeToDelete) {
      setEdgeToDelete(null); // Hide the delete button when clicking outside
    }
    closeContextMenu();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const nodesArray = [];
      const edgesArray = [];

      // Collect nodes in nodesArray
      nodes.forEach((node) => {
        const nodeObject = {
          id: node.id,
          type: node.type,
          style: { ...node.style },
          data: {
            ...node.data, // Includes parameter_type and label
          },
          position: {
            x: node.position.x.toFixed(2), // Fixing to 2 decimal places
            y: node.position.y.toFixed(2), // Fixing to 2 decimal places
          },
          sourcePosition: node.sourcePosition,
          targetPosition: node.targetPosition,
        };
        nodesArray.push(nodeObject);
      });

      // Collect edges in edgesArray
      edges.forEach((edge) => {
        edgesArray.push({
          id: `${edge.source}->${edge.target}`,
          source: edge.source,
          target: edge.target,
          label: edge.label,
          type: edge.type,
          markerEnd: { ...edge.markerEnd },
        });
      });

      // Organize nodes and edges into separate sections in elements
      const elements = [{ nodes: nodesArray }, { edges: edgesArray }];

      const elementsJSON = JSON.stringify(elements, null, 2);


      await new Promise((resolve) => setTimeout(resolve, 0));

      const trimmedTemplateName = templateNameInput.trim();
      const trimmedTemplateCode = templatecode; // Assuming template_code is the same as template_name
      // if (trimmedTemplateName === "") {
      //   setLoading(false);
      //   toast.error("Please enter a template name.");
      //   return;
      // }

      const response = await fetchWrapper(
        `${process.env.REACT_APP_BASE_API_URL1}/api/v1/model/update/${model_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_name: trimmedTemplateName,
            model_code: trimmedTemplateCode,
            structure: elementsJSON,
          }),
        }
      );



      setLoading(false);

      toast.success("Model saved successfully");
      navigate("/model_list");
    } catch (error) {
      setLoading(false);
      console.error("Error:", error);
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchNodes();
  }, [template_type_id]);

  useEffect(() => {
    if (reactFlowInstance) {

    }
  }, [reactFlowInstance]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchWrapper(
          `${process.env.REACT_APP_BASE_API_URL1}/api/v1/model/${model_id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        )



        setTemplateName(data.model_name);
        setTemplateNameInput(data.model_name);
        setTemplateCode(data.model_code);

        // Parse the structure JSON string
        let structure;
        try {
          structure = JSON.parse(data.structure);
        } catch (error) {
          console.error("Error parsing structure JSON:", error);
          return;
        }



        // Validate the structure
        const nodesArray = structure.find((item) => item.nodes)?.nodes;
        const edgesArray = structure.find((item) => item.edges)?.edges;

        if (!Array.isArray(nodesArray)) {
          console.error(
            "Nodes array is missing or is not an array:",
            nodesArray
          );
          return;
        }

        if (!Array.isArray(edgesArray)) {
          console.error(
            "Edges array is missing or is not an array:",
            edgesArray
          );
          return;
        }

        const iNodes = [];
        const iEdges = [];

        const getTemplateTypeId = (master, templateId) => {
          for (const templateGroup of master) {
            const template = templateGroup.templates.find(
              (template) => template._id === templateId
            );
            if (template) {
              return template.template_type_id;
            }
          }
          return null; // return null if the template is not found
        };

        // Convert nodes
        nodesArray.forEach((node) => {
          iNodes.push({
            id: node.id,
            type: node.type || "defaultNode",
            position: {
              x: parseFloat(node.position.x),
              y: parseFloat(node.position.y),
            },
            data: { label: node.data.label },
            sourcePosition: node.sourcePosition,
            targetPosition: node.targetPosition,
            style: { ...node.style } || { height: 20, width: 100 },
          });

          const templateTypeId = getTemplateTypeId(master, node.id);


          if (templateTypeId) {
            setUsedTemplateTypeIds((prev) => [...prev, templateTypeId]); // Add the template type ID to the used list
          }
        });

        edgesArray.forEach((edge) => {
          // Replace arrow (->) with hyphen (-) in edge.id
          const edgeId = edge.id.replace("->", "-");

          // Push the edge object with the modified id format
          iEdges.push({
            id: `reactflow__edge-${edgeId}`, // Modified id format
            source: edge.source,
            target: edge.target,
            animated: edge.animated || false,
            label: edge.label || "",
            markerEnd: { ...edge.markerEnd },
            type: edge.type,
          });
        });

        // Log and set the elements

        setNodes(iNodes);
        setEdges(iEdges);
      } catch (error) {
        console.error("Error fetching structure:", error);
      }
    };

    fetchData();
  }, [model_id, master]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="tb-responsive templatebuilder-body">
            <div className="tb-container pt-3">
              <nav
                className="breadcrumb-nav show-breadcrumb"
                aria-label="breadcrumb"
              >
                {" "}
                <h5>Update {templateName ? templateName : "Loading..."}</h5>
                <ol className="breadcrumb template-breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="#">Models</a>
                  </li>

                  <li className="breadcrumb-item">
                    <a href="#">Model Class</a>
                  </li>
                  <li className="breadcrumb-item">
                    <a href="">{templateName ? templateName : "Loading..."}</a>
                  </li>

                  <li className="breadcrumb-item active">Update</li>
                </ol>
              </nav>

              <div
                style={{ marginRight: 0, marginLeft: 0 }}
                className="row tb-header align-content-center "
              >
                <div className="col-md-3" style={{ width: "300px" }}>
                  <div className="tb-search-div">
                    <img src={search1} className="tg-search-icon" />
                    <input
                      className="tb-search-input"
                      placeholder="search"
                      style={{ padding: "8px 35px" }}
                    />
                  </div>
                </div>

                <div className="col text-center">
                  <div className="d-flex justify-content-between align-content-center">
                    <h6 className="align-content-center">Model Builder</h6>
                    <div className="d-flex gap-3">

                      <div className="tb-search-div d-flex gap-3">
                        <img
                          src={folder}
                          className="tg-search-icon"
                          alt="folder icon"
                        />
                        <input
                          className="tb-search-input"
                          placeholder="Asset Group Code"
                          style={{ padding: "8px 35px", width: "350px" }}
                          value={templatecode}
                          onChange={(e) => setTemplateCode(e.target.value)}
                        />
                      </div>
                      <div className="tb-search-div d-flex gap-3">
                        <img src={folder} className="tg-search-icon" />
                        <input
                          className="tb-search-input"
                          placeholder="Model Name"
                          style={{ padding: "8px 35px", width: "350px" }}
                          value={templateNameInput}
                          onChange={(e) => setTemplateNameInput(e.target.value)}
                          ref={templateNameRef}
                        />
                        <button onClick={handleSave} className="tb-save-btn">
                          SAVE
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="dndflow " onClick={hideButton}>
                <Sidebar
                  master={master}
                  usedTemplateTypeIds={usedTemplateTypeIds}
                />

                <ReactFlowProvider>
                  <div
                    className="reactflow-wrapper"
                    ref={reactFlowWrapper}
                    style={{ height: 600 }}
                  >
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      nodeTypes={nodeTypes}
                      edgeTypes={edgeTypes}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      onInit={setReactFlowInstance}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onNodeContextMenu={onNodeContextMenu}
                      onEdgeClick={onEdgeClick}
                      onLoad={(reactFlowInstance) =>
                        reactFlowInstance.fitView()
                      }
                      fitView
                    >
                      <Controls />
                      <SmoothStepEdge type="smoothstep" />
                      {/* Display delete button when edgeToDelete is set */}
                      {edgeToDelete && (
                        <div
                          className="edit-cont"
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            left: edgeToDelete.position.x, // Correct position
                            top: edgeToDelete.position.y, // Correct position
                            zIndex: 10,
                          }}
                        >
                          <button
                            className="edit-btn"
                            onClick={() => openEdgeLabelModal(edgeToDelete.id)}
                          >
                            Add Label
                          </button>
                          <button
                            className="delete-btn"
                            onClick={(e) => {
                              e.stopPropagation(); // Ensure only this click is registered
                              onDeleteEdge(); // Trigger edge deletion
                            }}
                          >
                            Delete Edge
                          </button>
                        </div>
                      )}
                      <Background
                        id="1"
                        gap={10}
                        color="#e8e8e8"
                        variant={BackgroundVariant.Lines}
                      />
                    </ReactFlow>
                  </div>
                </ReactFlowProvider>

                {contextMenu.isOpen && (
                  <div
                    className="edit-cont"
                    style={{
                      position: "absolute",
                      top: contextMenu.position.y,
                      left: contextMenu.position.x,
                    }}
                    onClick={closeContextMenu}
                  >
                    <button
                      className="edit-btn"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => onDeleteNode(selectedNode)}
                    >
                      Delete
                    </button>
                    {/* Use onDeleteNode */}
                  </div>
                )}

                <div
                  id="overlay"
                  className={`modal fade ${isModalOpen ? "show" : ""}`}
                  tabIndex="-1"
                  role="dialog"
                  style={{
                    zIndex: 30,
                    display: isModalOpen ? "block" : "none",
                    transition: "width 2s",
                  }}
                >
                  <div>
                    <div className="addunit-card Width 25%">
                      <div className="addunit-header">
                        <h4>Edit Node Data</h4>
                        <button
                          style={{ border: "none", backgroundColor: "inherit" }}
                          type="button"
                          className="close"
                          onClick={closeModal}
                        >
                          <img
                            src={close}
                            width="28px"
                            height="28px"
                            alt="Close"
                          />
                        </button>
                      </div>

                      <form onSubmit={saveChanges}>
                        {selectedNode && ( // Add this condition
                          <div className="addunit-form">
                            <div className="row">
                              <div className="col-sm-6">
                                <div className="d-flex flex-column">
                                  <label
                                    htmlFor="node-name"
                                    className="addunit-form-text"
                                  >
                                    Node Name *
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="node-name"
                                    value={editData.label}
                                    onChange={(e) =>
                                      handleEditNodeData(
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </div>
                              </div>

                              <div className="col-sm-6">
                                <div className="d-flex flex-column">
                                  <label
                                    htmlFor="parameter-name"
                                    className="addunit-form-text"
                                  >
                                    Parameter Type *
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="parameter-name"
                                    value={editData.parameter_type}
                                    onChange={(e) =>
                                      handleEditNodeData(
                                        "parameter_type",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="addunit-card-footer">
                          <button
                            type="button"
                            className="discard-btn"
                            onClick={closeModal}
                          >
                            Close
                          </button>
                          <button type="submit" className="update-btn">
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                {/* Modal to add label to an edge */}
                <div
                  id="overlay"
                  className={`modal fade ${edgeLabelModal.isOpen ? "show" : ""
                    }`}
                  tabIndex="-1"
                  role="dialog"
                  style={{
                    zIndex: 30,
                    display: edgeLabelModal.isOpen ? "block" : "none",
                    transition: "width 2s",
                  }}
                >
                  <div>
                    <div className="addunit-card Width 25%">
                      <div className="addunit-header">
                        <h4>Add or Edit Edge Label</h4>
                        <button
                          style={{ border: "none", backgroundColor: "inherit" }}
                          type="button"
                          className="close"
                          onClick={() =>
                            setEdgeLabelModal({
                              isOpen: false,
                              label: "",
                              edgeId: null,
                            })
                          }
                        >
                          <img
                            src={close}
                            width="28px"
                            height="28px"
                            alt="Close"
                          />
                        </button>
                      </div>

                      <div className="addunit-form">
                        <div className="row">
                          <div className="d-flex flex-column">
                            <label
                              htmlFor="edge-name"
                              className="addunit-form-text"
                            >
                              Edge Label
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="edge-name"
                              value={edgeLabelModal.label}
                              onChange={(e) =>
                                setEdgeLabelModal({
                                  ...edgeLabelModal,
                                  label: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="addunit-card-footer">
                        <button
                          type="button"
                          className="discard-btn"
                          onClick={() =>
                            setEdgeLabelModal({
                              isOpen: false,
                              label: "",
                              edgeId: null,
                            })
                          }
                        >
                          Close
                        </button>
                        <button
                          type="button"
                          className="update-btn"
                          onClick={() =>
                            addLabelToEdge(
                              edgeLabelModal.edgeId,
                              edgeLabelModal.label
                            )
                          }
                        >
                          Add Label
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer autoClose={4000} position={"top-right"} />
    </>
  );
};

export default EditAsset;
